import React, { useState, useMemo, useRef, useLayoutEffect, useEffect } from "react";
import { Autocomplete } from "@mui/material";
import TextField from "../../../components/TextField";

const SCROLL_THRESHOLD_PX = 10;
const SEARCH_PAGE_SIZE = 50;

export default function NewPreferredCounselAutocomplete({
    value,
    onChange,
    options,
    displayedCount,
    onLoadMore,
    onClose,
    disabled,
}) {
    const [inputValue, setInputValue] = useState("");
    const [searchDisplayCount, setSearchDisplayCount] = useState(SEARCH_PAGE_SIZE);

    // Debounce inputValue so the O(n) filter only runs after the user pauses typing,
    // rather than on every keystroke across the unbounded options array.
    const [debouncedInput, setDebouncedInput] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedInput(inputValue), 250);
        return () => clearTimeout(timer);
    }, [inputValue]);

    // Refs for preserving scroll position when loading more items
    const listboxRef = useRef(null);
    const savedScrollTop = useRef(null);

    // Custom filter: when user types, search from ALL options; otherwise use lazy loading.
    // A single O(n) pass produces both the paginated slice and the total match count so
    // handleScroll can clamp searchDisplayCount without a second redundant filter pass.
    const { filteredOptions, totalSearchMatches } = useMemo(() => {
        if (debouncedInput.trim() === "") {
            // No search term: return first displayedCount items for lazy loading
            return { filteredOptions: options.slice(0, displayedCount), totalSearchMatches: 0 };
        }
        // User is searching: filter from ALL options in one pass, then slice for display
        const searchTerm = debouncedInput.toLowerCase();
        const allMatches = options.filter(opt =>
            (opt.label || "").toLowerCase().includes(searchTerm)
        );
        return {
            filteredOptions: allMatches.slice(0, searchDisplayCount),
            totalSearchMatches: allMatches.length,
        };
    }, [options, debouncedInput, displayedCount, searchDisplayCount]);

    const handleScroll = (e) => {
        const node = e.currentTarget;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - SCROLL_THRESHOLD_PX) {
            // Save scroll position before loading more items
            savedScrollTop.current = node.scrollTop;
            if (debouncedInput.trim() === "") {
                // No search: load more from all options
                onLoadMore();
            } else {
                // User is searching: load more search results.
                // totalSearchMatches is already computed by useMemo — no redundant O(n) filter needed.
                setSearchDisplayCount(prev => Math.min(prev + SEARCH_PAGE_SIZE, totalSearchMatches));
            }
        }
    };

    // Restore scroll position after the listbox re-renders with more items.
    // useLayoutEffect fires after DOM mutations but before the browser paints,
    // so the user never sees the list jump to the top.
    useLayoutEffect(() => {
        if (savedScrollTop.current !== null && listboxRef.current) {
            listboxRef.current.scrollTop = savedScrollTop.current;
            savedScrollTop.current = null;
        }
    }, [filteredOptions]);

    const handleClose = () => {
        setInputValue("");
        setDebouncedInput("");
        setSearchDisplayCount(SEARCH_PAGE_SIZE);
        onClose();
    };

    return (
        <Autocomplete
            options={filteredOptions}
            getOptionLabel={(opt) => opt.label || ""}
            value={value || null}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason === "input" || reason === "clear") {
                    setSearchDisplayCount(SEARCH_PAGE_SIZE); // Reset search display count when search term changes
                }
            }}
            onChange={(_, newVal) => onChange(newVal ?? "")}
            onClose={handleClose}
            disabled={disabled}
            filterOptions={(x) => x} // Disable built-in filtering since we handle it manually
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="New Preferred Counsel"
                    placeholder="Search and select..."
                    required={false}
                    InputProps={{
                        ...params.InputProps,
                    }}
                />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            ListboxProps={{
                style: { maxHeight: 250 },
                onScroll: handleScroll,
                ref: listboxRef,
            }}
            size="small"
            sx={{ width: "100%" }}
        />
    );
}
